import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { Fragment, useContext, useState } from "react";
import ReactGA from "react-ga4";
import { Navigate } from "react-router";
import { useNavigate } from "react-router-dom";

import SubscriptionModal, { SubscriptionModalType } from "../SubscriptionModal";

import UserContext from "context/UserContext";

import Modal from "components/atoms/Modal";
import Spinner from "components/atoms/Spinner";
import SubscriptionCard from "components/molecules/SubscriptionCard";

import { error, success } from "utils";
import { AI_HUB_SLUG, uniqueEventId } from "utils/products";
import { CustomerStatusType, UsageInfoType } from "utils/types";

// Tier configuration matching backend TierTokenMapping
export interface TieredAIHubTierConfig {
  tier: "trial" | "mid" | "high";
  name: string;
  price: number;
  tokensMillions: number;
  refillsAllowed: boolean;
  description: string;
  descriptionAlt: string;
}

// Hardcoded tier settings - these should match the backend
const tierSettings: TieredAIHubTierConfig[] = [
  {
    tier: "trial",
    name: "Trial",
    price: 25,
    tokensMillions: 5,
    refillsAllowed: false,
    description: "Perfect for testing and evaluation",
    descriptionAlt: "(limited trial - no refills)",
  },
  {
    tier: "mid",
    name: "Professional",
    price: 1500,
    tokensMillions: 100,
    refillsAllowed: true,
    description: "For growing applications with moderate usage",
    descriptionAlt: "(auto-refill enabled)",
  },
  {
    tier: "high",
    name: "Enterprise",
    price: 3000,
    tokensMillions: 250,
    refillsAllowed: true,
    description: "For high-volume production applications",
    descriptionAlt: "(auto-refill enabled)",
  },
];

interface TieredAIHubStateType {
  isSubscribed: boolean | null;
  tier: string | null;
  usageInfo: UsageInfoType | null;
}

const ManageSubscriptionTieredAIHub = () => {
  const navigate = useNavigate();

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const productQuery = params.get("product");

  const [unauthorized, setUnauthorized] = useState(false);

  const [customerStatus, setCustomerStatus] = useState<CustomerStatusType>({
    hasOrg: false,
    hasPaymentMethod: false,
  });
  const [productState, setProductState] = useState<TieredAIHubStateType>({
    isSubscribed: null,
    tier: null,
    usageInfo: null,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_productDetail, setProductDetail] = useState<any>({});

  const [subscriptionModal, setSubscriptionModal] =
    useState<SubscriptionModalType>({
      isOpen: false,
      planSlug: null,
      shouldUpdate: false,
    });

  const [selectedTier, setSelectedTier] = useState<TieredAIHubTierConfig>(
    tierSettings[0]
  );

  const [unsubscribeModalIsOpen, setUnsubscribeModalIsOpen] = useState(false);
  const [upgradeConfirmOpen, setUpgradeConfirmOpen] = useState(false);
  const [pendingUpgradeTier, setPendingUpgradeTier] =
    useState<TieredAIHubTierConfig | null>(null);

  const setAllData = (data: any) => {
    setProductDetail(data);
    setCustomerStatus((prev) => ({
      ...prev,
      hasOrg: data.hasOrg,
      hasPaymentMethod: data.hasPaymentMethod,
    }));
    setProductState((prev) => ({
      ...prev,
      isSubscribed: data.isSubscribed,
      tier: data.tier,
      usageInfo: data.usageInfo,
    }));
  };

  const userContext = useContext(UserContext);

  // Fetch AI Hub subscription details
  const getProduct = async () =>
    await axios.get(`/accounts/products/ai-hub/detail`);

  const { isLoading, isFetching, isPaused, refetch } = useQuery(
    ["ai-hub-product"],
    getProduct,
    {
      enabled: !!productQuery && !!userContext,
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Could not fetch the products");
        if (err?.response?.status === 403) {
          setUnauthorized(true);
        }
      },
      onSuccess: (res: AxiosResponse) => {
        setAllData(res?.data);
      },
    }
  );

  // Upgrade subscription mutation
  const upgradeSubscription = useMutation(
    async (newTier: string) => {
      return await axios.post(
        `/accounts/products/ai-hub/subscription/upgrade`,
        {
          tier: newTier,
        }
      );
    },
    {
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Something went wrong");
      },
      onSuccess: (res: AxiosResponse) => {
        success("Subscription upgraded successfully");
        setAllData(res?.data);
        setUpgradeConfirmOpen(false);
        setPendingUpgradeTier(null);

        ReactGA.event({
          action: "subscriptionupgraded",
          category: `${AI_HUB_SLUG}_${productState.tier}`,
          label: uniqueEventId(),
        });
      },
    }
  );

  // Unsubscribe mutation
  const unsubscribe = useMutation(
    async () => await axios.delete(`/accounts/products/ai-hub/subscription`),
    {
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Something went wrong");
      },
      onSuccess: () => {
        success("Unsubscribed successfully");
        setUnsubscribeModalIsOpen(false);

        ReactGA.event({
          action: "subscriptioncancelled",
          category: `${AI_HUB_SLUG}_${productState.tier}`,
          label: uniqueEventId(),
        });

        setProductState((prev) => ({
          ...prev,
          isSubscribed: false,
          tier: null,
          usageInfo: null,
        }));
      },
    }
  );

  if (unauthorized) {
    return <Navigate to="/access-restricted" />;
  }

  function closeSubscriptionModal() {
    refetch();
    setSubscriptionModal({
      isOpen: false,
      planSlug: null,
      shouldUpdate: false,
    });
  }

  const getCurrentTierIndex = (): number => {
    if (!productState.tier) return -1;
    return tierSettings.findIndex((t) => t.tier === productState.tier);
  };

  const isCurrentTier = (tierIndex: number): boolean => {
    return getCurrentTierIndex() === tierIndex;
  };

  const buttonOnClickForTier = (
    tierConfig: TieredAIHubTierConfig,
    tierIndex: number
  ) => {
    const currentIndex = getCurrentTierIndex();

    // If not subscribed, open subscription modal
    if (!productState.isSubscribed || currentIndex < 0) {
      return () => {
        setSelectedTier(tierConfig);
        setSubscriptionModal({
          isOpen: true,
          planSlug: `ai-hub-${tierConfig.tier}`,
          shouldUpdate: false,
        });
      };
    }

    // If this is a higher tier, show upgrade confirmation
    if (tierIndex > currentIndex) {
      return () => {
        setPendingUpgradeTier(tierConfig);
        setUpgradeConfirmOpen(true);
      };
    }

    // Can't downgrade
    return () => {};
  };

  const buttonTextForTier = (
    tierIndex: number,
    isSubscribed: boolean
  ): string => {
    const curTierIndex = getCurrentTierIndex();
    if (!isSubscribed || curTierIndex < 0) {
      return "Subscribe";
    }
    if (tierIndex < curTierIndex) {
      return "No Downgrade Available";
    }
    if (tierIndex > curTierIndex) {
      return "Upgrade";
    }
    return "";
  };

  const isAdmin = () => {
    if (userContext === null) {
      return false;
    }
    if (userContext.isOrgOwner) {
      return true;
    }
    let admin = false;
    userContext.roles.forEach((element) => {
      if (element.toLowerCase() === "admin") {
        admin = true;
      }
    });
    return admin;
  };

  const formatPrice = (price: number): string => {
    if (price >= 1000) {
      return `$${(price / 1000).toFixed(price % 1000 === 0 ? 0 : 1)}k`;
    }
    return `$${price}`;
  };

  return (
    <Fragment>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-bold inter-bold text-md md:text-lg xl:text-3xl">
            AI Hub Subscription (New Tiers)
          </h2>
        </div>
        {isFetching ||
        isLoading ||
        isPaused ||
        upgradeSubscription.isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="flex flex-wrap place-content-evenly h-full">
              {tierSettings.map((tierConfig, index) => (
                <SubscriptionCard
                  key={tierConfig.tier}
                  name={tierConfig.name}
                  price={
                    <>
                      <div className="text-gray-900 font-inter text-6xl font-extrabold leading-[60px]">
                        {formatPrice(tierConfig.price)}
                      </div>
                      <div className="text-gray-500 font-inter text-2xl font-medium leading-8">
                        /mo
                      </div>
                    </>
                  }
                  counterInput={<></>}
                  description={tierConfig.description}
                  descriptionAlt={tierConfig.descriptionAlt}
                  bulletsHtml={[
                    <>
                      <span className="font-extrabold">
                        {tierConfig.tokensMillions}M{" "}
                      </span>
                      <span className="">tokens per month included</span>
                    </>,
                    <>
                      <span className="font-extrabold">$1.50 </span>
                      <span className="">per million tokens</span>
                    </>,
                    tierConfig.refillsAllowed ? (
                      <>
                        <span className="font-extrabold">Auto-refill </span>
                        <span className="">enabled by default</span>
                      </>
                    ) : (
                      <>
                        <span className="font-extrabold text-amber-600">
                          No refills{" "}
                        </span>
                        <span className="">- trial only</span>
                      </>
                    ),
                  ]}
                  bullets={[
                    "Access food database REST API",
                    "Access to Nutrition Advisor",
                    "Mobile app SDK quick-start app",
                    tierConfig.refillsAllowed
                      ? "Credit refills as required"
                      : "Upgrade to enable refills",
                    "Detailed documentation",
                  ]}
                  isCurrent={
                    isCurrentTier(index) && Boolean(productState?.isSubscribed)
                  }
                  buttonText={buttonTextForTier(
                    index,
                    productState?.isSubscribed || false
                  )}
                  buttonOnClick={buttonOnClickForTier(tierConfig, index)}
                  hideButton={
                    getCurrentTierIndex() > index &&
                    Boolean(productState?.isSubscribed)
                  }
                  isCurrentButtonText="Product Details"
                  isCurrentButtonOnClick={() =>
                    navigate(`/details?product=${productQuery}`)
                  }
                  disabled={
                    isLoading ||
                    isFetching ||
                    isPaused ||
                    upgradeSubscription.isLoading ||
                    !isAdmin()
                  }
                  wide={false}
                  showCancelButton={
                    isCurrentTier(index) &&
                    (productState?.isSubscribed || false)
                  }
                  cancelButtonOnClick={() => setUnsubscribeModalIsOpen(true)}
                />
              ))}
            </div>

            {/* Usage Information */}
            {productState?.isSubscribed && productState?.usageInfo && (
              <div className="mt-8 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Current Usage</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Token Budget</p>
                    <p className="text-xl font-bold">
                      {(
                        (productState.usageInfo.unitQuota || 0) / 1000000
                      ).toFixed(1)}
                      M
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Used This Period</p>
                    <p className="text-xl font-bold">
                      {((productState.usageInfo.units || 0) / 1000000).toFixed(
                        2
                      )}
                      M
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Remaining</p>
                    <p className="text-xl font-bold text-green-600">
                      {(
                        ((productState.usageInfo.unitQuota || 0) -
                          (productState.usageInfo.units || 0)) /
                        1000000
                      ).toFixed(2)}
                      M
                    </p>
                  </div>
                </div>
                {productState.tier === "trial" && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-amber-800 text-sm">
                      <strong>Trial Plan:</strong> Refills are not available on
                      the trial plan. Upgrade to Professional or Enterprise to
                      enable auto-refills.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tier Comparison Table */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4">Compare Plans</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Feature
                      </th>
                      {tierSettings.map((tier) => (
                        <th
                          key={tier.tier}
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {tier.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Monthly Price
                      </td>
                      {tierSettings.map((tier) => (
                        <td
                          key={tier.tier}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center"
                        >
                          ${tier.price}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Included Tokens
                      </td>
                      {tierSettings.map((tier) => (
                        <td
                          key={tier.tier}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center"
                        >
                          {tier.tokensMillions}M
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Auto-Refill
                      </td>
                      {tierSettings.map((tier) => (
                        <td
                          key={tier.tier}
                          className="px-6 py-4 whitespace-nowrap text-sm text-center"
                        >
                          {tier.refillsAllowed ? (
                            <span className="text-green-600">Yes</span>
                          ) : (
                            <span className="text-red-500">No</span>
                          )}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Manual Refill
                      </td>
                      {tierSettings.map((tier) => (
                        <td
                          key={tier.tier}
                          className="px-6 py-4 whitespace-nowrap text-sm text-center"
                        >
                          {tier.refillsAllowed ? (
                            <span className="text-green-600">Yes</span>
                          ) : (
                            <span className="text-red-500">No</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={subscriptionModal.isOpen}
        closeModal={closeSubscriptionModal}
        closeBtn={true}
        shouldCreateOrg={!customerStatus.hasOrg}
        shouldCapturePayment={!customerStatus.hasPaymentMethod}
        shouldUpdate={subscriptionModal.shouldUpdate}
        planSlug={subscriptionModal.planSlug || ""}
        subscriptionData={{ tier: selectedTier.tier }}
      />

      {/* Upgrade Confirmation Modal */}
      <Modal
        isOpen={upgradeConfirmOpen}
        setOpen={setUpgradeConfirmOpen}
        title={`Upgrade to ${pendingUpgradeTier?.name}?`}
        disabled={upgradeSubscription.isLoading}
        isLoading={upgradeSubscription.isLoading}
        description={`You will be upgraded to the ${
          pendingUpgradeTier?.name
        } plan at ${formatPrice(pendingUpgradeTier?.price || 0)}/month with ${
          pendingUpgradeTier?.tokensMillions
        }M tokens included. Your billing will be prorated.`}
        submitButtonName="Confirm Upgrade"
        buttonType="primary"
        onSubmitted={() => {
          if (pendingUpgradeTier) {
            upgradeSubscription.mutate(pendingUpgradeTier.tier);
          }
        }}
      />

      {/* Unsubscribe Modal */}
      <Modal
        isOpen={unsubscribeModalIsOpen}
        setOpen={setUnsubscribeModalIsOpen}
        title="Cancel Subscription?"
        disabled={unsubscribe.isLoading}
        isLoading={unsubscribe.isLoading}
        description="Are you sure you want to cancel your AI Hub subscription? You will lose access at the end of your current billing period."
        submitButtonName="Cancel Subscription"
        buttonType="warning"
        type="error"
        onSubmitted={() => unsubscribe.mutate()}
      />
    </Fragment>
  );
};

export default ManageSubscriptionTieredAIHub;
